import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Typography, Tooltip, Box } from '@mui/material';
import { clearEditMode, dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
  AutoCompleteSelectServiceUI,
} from '../Interface';
import {
  MaterialTransportItemIcons,
  MaterialTransportItemStatus,
  MaterialTransportItemStatusList,
} from '../../config/materialTransport';
import colorFinder from '../../utils/colorFinder';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';
import { sxConfig } from './materialTransport.style';
import masterDataService from '../../service/masterData.service';

const EditToolbar = (props) => {
  const { push, defTransportItem } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defTransportItem,
      id: uuidv4(),
      visOrder: (values.materialTransportItem?.length ?? 0) + 1,
    };

    await push(newData);

    const fieldsToValidate = [
      `materialTransportItem.${values.materialTransportItem.length ?? 0}.itemCode`,
      `materialTransportItem.${values.materialTransportItem.length ?? 0}.qty`,
      `materialTransportItem.${values.materialTransportItem.length ?? 0}.qtyUnitId`,
      `materialTransportItem.${values.materialTransportItem.length ?? 0}.diffQty`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi sx={sxConfig.addMaterialButton} onClick={handleClick} text="Anyag hozzáadása" />
    </GridToolbarContainer>
  );
};

const MaterialTransportStatusCheckbox = ({ status, onChange }) => {
  const currentIndex = MaterialTransportItemStatusList.findIndex((item) => item.value === status);
  const currentStatus = MaterialTransportItemStatusList[currentIndex];
  const currentStatusColor = colorFinder(MaterialTransportItemStatusList[currentIndex]?.color);

  const handleClick = () => {
    const nextIndex = (Math.max(currentIndex, 0) + 1) % MaterialTransportItemStatusList.length;
    onChange(MaterialTransportItemStatusList[nextIndex].value);
  };

  return (
    <Tooltip title={currentStatus.label}>
      <CheckboxUI
        checked={status !== MaterialTransportItemStatus.PENDING}
        icon={MaterialTransportItemIcons[currentStatus.value]}
        checkedIcon={MaterialTransportItemIcons[currentStatus.value]}
        onChange={handleClick}
        sx={sxConfig.statusCheckbox(currentStatusColor)}
      />
    </Tooltip>
  );
};

const normalizeSingleItemResponse = (response) => {
  if (Array.isArray(response?.body)) {
    return response.body[0] ?? null;
  }

  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response?.body ?? response ?? null;
};

const MaterialTransportItemTable = ({ defTransportItem, autocompleteOptions }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.materialTransportItem.findIndex((rc) => rc.id === id);
  };

  const handleItemChange = async (valueIndex, itemCode) => {
    const response = itemCode ? await masterDataService.itemsFromTableToFormByItemCode(itemCode) : null;

    const selectedItem = normalizeSingleItemResponse(response);

    setFieldValue(`materialTransportItem.${valueIndex}.itemCode`, itemCode);
    setFieldValue(`materialTransportItem.${valueIndex}.itemName`, selectedItem?.itemName ?? '');
    setFieldValue(`materialTransportItem.${valueIndex}.qtyUnitId`, selectedItem?.quantityUnitId ?? null);
  };

  const handleDelete = (index) => {
    const findedItem = values.materialTransportItem[index];

    clearEditMode(apiRef);

    if (findedItem) {
      const updatedList = values.materialTransportItem.map((item) => {
        if (item.visOrder > findedItem.visOrder) {
          return { ...item, visOrder: item.visOrder - 1 };
        }

        return item;
      });

      setFieldValue('materialTransportItem', updatedList);
    }
  };

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'itemCode',
        headerName: 'Anyag megnevezése',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const item = values.materialTransportItem?.[valueIndex];

          return [item?.itemCode, item?.itemName].filter(Boolean).join(' - ');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const rowItemCode =
            data.value ?? data.row?.itemCode ?? values.materialTransportItem?.[valueIndex]?.itemCode ?? null;
          return (
            <Field
              name={`materialTransportItem.${valueIndex}.itemCode`}
              type="text"
              variant="standard"
              as={AutoCompleteSelectServiceUI}
              helperText={<FormErrorUI message={errors?.materialTransportItem?.[valueIndex]?.itemCode} />}
              selectedValue={rowItemCode}
              selectedLabelValue={[rowItemCode, values.materialTransportItem?.[valueIndex]?.itemName]
                .filter(Boolean)
                .join(' - ')}
              service={itemAutocompleteService}
              serviceRequestBuilder={itemAutocompleteRequestBuilder}
              searchModeEnabled
              onChange={async (_e, newVal) => {
                const val = newVal?.value ?? null;
                await handleItemChange(valueIndex, val);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`materialTransportItem.${valueIndex}.itemCode`)}
            />
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'number',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.materialTransportItem?.[valueIndex]?.qty;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`materialTransportItem.${valueIndex}.qty`}
              as={InputUI}
              variant="standard"
              type="number"
              fullWidth
              helperText={<FormErrorUI message={errors?.materialTransportItem?.[valueIndex]?.qty} />}
              onChange={(e) => {
                setFieldValue(`materialTransportItem.${valueIndex}.qty`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`materialTransportItem.${valueIndex}.qty`)}
            />
          );
        },
      },
      {
        field: 'qtyUnitId',
        headerName: 'Mennyiségi egység',
        type: 'string',
        editable: false,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedQtyUnit = autocompleteOptions.quantityUnit.find(
            (u) => u.value === values.materialTransportItem?.[valueIndex]?.qtyUnitId
          );
          return findedQtyUnit?.label;
        },
      },
      {
        field: 'diffQty',
        headerName: 'Eltérés mennyiség',
        type: 'number',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.materialTransportItem?.[valueIndex]?.diffQty;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const isPartial = values.materialTransportItem?.[valueIndex]?.status === MaterialTransportItemStatus.PARTIAL;

          return (
            <Field
              name={`materialTransportItem.${valueIndex}.diffQty`}
              as={InputUI}
              variant="standard"
              type="number"
              fullWidth
              value={values.materialTransportItem?.[valueIndex]?.diffQty ?? ''}
              disabled={!isPartial}
              helperText={<FormErrorUI message={errors?.materialTransportItem?.[valueIndex]?.diffQty} />}
              onChange={(e) => {
                setFieldValue(`materialTransportItem.${valueIndex}.diffQty`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`materialTransportItem.${valueIndex}.diffQty`)}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Státusz',
        sortable: false,
        type: 'actions',
        getActions: ({ row }) => {
          const valueIndex = getValueIndex(values, row.id);
          const status = values.materialTransportItem?.[valueIndex]?.status ?? MaterialTransportItemStatus.PENDING;
          return [
            <MaterialTransportStatusCheckbox
              status={status}
              onChange={(newStatus) => {
                setFieldValue(`materialTransportItem.${valueIndex}.status`, newStatus);
                setFieldValue(`materialTransportItem.${valueIndex}.diffQty`, null);
              }}
            />,
          ];
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(event) => {
                event.stopPropagation();
                const valueIndex = getValueIndex(values, id);

                handleDelete(valueIndex);
                return remove(valueIndex);
              }}
              color="inherit"
            />,
          ];
        },
      },
    ]);

  return (
    <div className="overflow-x-auto">
      <Typography sx={sxConfig.sectionTitle}>Anyaglista</Typography>
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="materialTransportItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.MaterialTransportItemTable}
                height={350}
                columns={columns(remove)}
                rows={values?.materialTransportItem ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    push,
                    defTransportItem,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
                pagination={false}
                hideFooter
                rowReordering
                onRowOrderChange={({ oldIndex, targetIndex }) => {
                  const updatedList = [...values.materialTransportItem];

                  const [movedItem] = updatedList.splice(oldIndex, 1);
                  updatedList.splice(targetIndex, 0, movedItem);

                  setFieldValue(
                    'materialTransportItem',
                    updatedList.map((item, index) => ({
                      ...item,
                      visOrder: index + 1,
                    }))
                  );
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default MaterialTransportItemTable;
